import { updateClient } from './api';

// Care details shown on the client overview: Member ID, diagnosis code and the
// case manager's name and phone. They live on the client record in the notes
// API. Until the backend that stores them is deployed, the live API drops them
// on save, so a per-browser copy is kept as well and used whenever the API
// returns a blank value. Once the API returns real values those win.
export const CARE_FIELDS = [
  { key: 'memberId', label: 'Member ID', placeholder: 'Payer member number' },
  { key: 'diagnosisCode', label: 'Diagnosis code', placeholder: 'ICD-10, e.g. Z74.09' },
  { key: 'caseManagerName', label: 'Case manager', placeholder: 'Name' },
  { key: 'caseManagerPhone', label: 'Case manager phone', placeholder: '(555) 555-0100' },
];

const STORAGE_KEY = 'hca.ops.clientCareDetails';

function readCache() {
  try { return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '{}') || {}; }
  catch { return {}; }
}

function writeCache(cache) {
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(cache)); }
  catch { /* storage unavailable: the API copy is still attempted */ }
}

export function pickCareDetails(source = {}) {
  return Object.fromEntries(CARE_FIELDS.map(f => [f.key, String(source[f.key] || '').trim()]));
}

// Returns the client with API values first and cached values filling any blanks.
export function withCareDetails(client) {
  if (!client) return client;
  const cached = readCache()[client.clientId] || {};
  const merged = {};
  for (const f of CARE_FIELDS) merged[f.key] = String(client[f.key] || '').trim() || String(cached[f.key] || '').trim();
  return { ...client, ...merged };
}

export function hasAnyCareDetail(client) {
  return CARE_FIELDS.some(f => String(client?.[f.key] || '').trim());
}

// Saves the four fields to the API (as part of the normal client update) and to
// the browser copy. Returns the client with the new values applied.
export async function saveCareDetails(client, details) {
  const next = pickCareDetails(details);
  await updateClient(client.clientId, {
    clientName: client.clientName,
    dob: client.dob || '',
    medicaidId: client.medicaidId || '',
    assignedCaregivers: client.assignedCaregivers || [],
    ...next,
  });
  const cache = readCache();
  cache[client.clientId] = next;
  writeCache(cache);
  return { ...client, ...next };
}

// After a prior authorization is confirmed: copy document values into any
// client field that is still blank. Values an admin already entered are kept.
export async function fillBlankCareDetails(client, fromAuth) {
  const current = pickCareDetails(withCareDetails(client));
  const incoming = pickCareDetails(fromAuth);
  const merged = { ...current };
  let changed = false;
  for (const f of CARE_FIELDS) {
    if (!merged[f.key] && incoming[f.key]) { merged[f.key] = incoming[f.key]; changed = true; }
  }
  if (!changed) return client;
  return saveCareDetails(client, merged);
}
